/**
 * AppPermissionsTable: the App inventory as a Carbon DataTable (mockup 03,
 * DESIGN.md "Data table"): each app's name over its publisher and the flags
 * that need a look (admin role, credential state, no owner), its risk tier as
 * a severity glyph and word, its permission counts, activity and review
 * status, then a row button "View details for {app}" that opens the detail
 * panel. Row click and Enter on the row open it too. Server pagination sits
 * under the table ("Previous page" / "Next page").
 *
 * Bad states are neutral (the Neutral Bad State Rule): a flag reads as a
 * high-contrast tag or a word, and only a Critical tier takes a hue, on its
 * glyph. The original props are unchanged; `title`, `description`, `tabs`,
 * `toolbar`, `notices` and `id` are optional additions passed to the
 * DataTable's slots.
 */

import type { ReactNode } from 'react'
import { DataTable, TableItem, type DataColumn } from '@/components/carbon/DataTable'
import { IconButton } from '@/components/carbon/IconButton'
import { IconCheck, IconChevronRight } from '@/components/carbon/icons'
import { SeverityGlyph } from '@/components/carbon/SeverityGlyph'
import { severityFromApi } from '@/components/carbon/severity'
import { Tag } from '@/components/carbon/Tag'
import type { OAuthApp } from './appPermissionsTypes'

interface AppPermissionsTableProps {
  apps: OAuthApp[]
  total: number
  pageNumber: number
  pageSize: number
  totalPages: number
  isLoading: boolean
  onPageChange: (page: number) => void
  onAppClick: (app: OAuthApp) => void
  /** The table's visible title and accessible name. Default "App inventory". */
  title?: string
  /** One line under the title. */
  description?: ReactNode
  /** ContainedTabs above the toolbar (the risk-tier tabs). */
  tabs?: ReactNode
  /** Toolbar controls (search and the filter menu). */
  toolbar?: ReactNode
  /** Notices between the head and the tabs (hidden Microsoft apps). */
  notices?: ReactNode
  /** The active filters as dismissible tags, under the toolbar. */
  filterTags?: ReactNode
  /** id on the table section, for in-page links. */
  id?: string
}

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

export function AppPermissionsTable({
  apps,
  total,
  pageNumber,
  pageSize,
  totalPages,
  isLoading,
  onPageChange,
  onAppClick,
  title = 'App inventory',
  description,
  tabs,
  toolbar,
  notices,
  filterTags,
  id,
}: AppPermissionsTableProps) {
  const columns: DataColumn<OAuthApp>[] = [
    {
      id: 'app',
      header: 'Application',
      primary: true,
      width: '34%',
      cell: app => (
        <>
          <TableItem
            title={<span className="break-words">{app.displayName}</span>}
            sub={
              <span className="inline-flex min-w-0 flex-wrap items-center gap-x-1.5">
                <span className="min-w-0 break-words">{app.publisherName || 'Unknown publisher'}</span>
                {app.isVerifiedPublisher ? (
                  <span className="inline-flex items-center gap-0.5 text-text-secondary">
                    <IconCheck size={12} />
                    Verified
                  </span>
                ) : null}
              </span>
            }
          />
          <AppFlags app={app} />
        </>
      ),
    },
    {
      id: 'risk',
      header: 'Risk',
      nowrap: true,
      cell: app => <SeverityGlyph level={severityFromApi(app.overallRiskTier)}>{app.overallRiskTier}</SeverityGlyph>,
    },
    {
      id: 'permissions',
      header: 'Permissions',
      hideBelow: 'c-md',
      cell: app => (
        <span className="block">
          <span className="block tabular-nums text-text-primary">{app.totalPermissionCount.toLocaleString('en-US')}</span>
          <small className="mt-0.5 block whitespace-nowrap text-helper tabular-nums text-text-helper">
            {app.applicationPermissionCount} application · {app.delegatedPermissionCount} delegated
          </small>
        </span>
      ),
    },
    {
      id: 'risky',
      header: 'Risky permissions',
      hideBelow: 'c-lg',
      cell: app => <RiskyPermissions app={app} />,
    },
    {
      id: 'activity',
      header: 'Activity',
      inlineOnPhone: true,
      cell: app => <ActivityTag lastSignIn={app.lastSignInDateTime} isStale={app.isStale} />,
    },
    {
      id: 'review',
      header: 'Review',
      inlineOnPhone: true,
      cell: app => <ReviewStatus status={app.acknowledgmentStatus} expiresAt={app.acknowledgmentExpiresAt} />,
    },
    {
      id: 'open',
      header: <span className="sr-only">Details</span>,
      align: 'end',
      width: '64px',
      cell: app => (
        <IconButton
          label={`View details for ${app.displayName}`}
          icon={<IconChevronRight />}
          size={40}
          onClick={() => onAppClick(app)}
          className="max-c-md:-ml-3"
        />
      ),
    },
  ]

  return (
    <div id={id} className="scroll-mt-24">
      <DataTable
        title={title}
        description={description}
        notices={notices}
        filterTags={filterTags}
        tabs={tabs}
        toolbar={toolbar}
        columns={columns}
        rows={apps}
        rowId={app => app.id}
        rowLabel={app => app.displayName}
        onRowActivate={onAppClick}
        isLoading={isLoading}
        empty="No apps match these filters. Change a filter or clear them to see the whole inventory."
        pagination={
          total > 0
            ? {
                page: pageNumber,
                pageSize,
                total,
                totalPages: Math.max(1, totalPages),
                onPageChange,
                itemLabel: total === 1 ? 'app' : 'apps',
                isLoading,
              }
            : undefined
        }
      />
    </div>
  )
}

/* -------------------------------------------------------------------------- */
/* Cells                                                                      */
/* -------------------------------------------------------------------------- */

/** The flags that need a look, as tags under the publisher: high contrast for the risks, outline for the gaps. */
function AppFlags({ app }: { app: OAuthApp }) {
  const flags: ReactNode[] = []
  if (app.hasDirectoryRoles) {
    flags.push(
      <Tag key="role" kind="high-contrast" size="sm" title="The app holds an Entra directory role">
        Admin role
      </Tag>,
    )
  }
  if (!app.isExternalApp && app.hasExpiredCredentials) {
    flags.push(
      <Tag key="expired" kind="high-contrast" size="sm" title="A credential on this app has expired">
        Credential expired
      </Tag>,
    )
  } else if (!app.isExternalApp && app.hasExpiringCredentials) {
    flags.push(
      <Tag key="expiring" size="sm" title="A credential on this app expires within 30 days">
        Credential expires soon
      </Tag>,
    )
  }
  if (app.ownerCount === 0) {
    flags.push(
      <Tag key="owner" kind="outline" size="sm" title="No owner is assigned in Entra">
        No owner
      </Tag>,
    )
  }
  if (flags.length === 0) return null
  return <span className="mt-1.5 flex flex-wrap gap-1">{flags}</span>
}

/** Critical and high permission counts by shape and word; a plain line when there are none. */
function RiskyPermissions({ app }: { app: OAuthApp }) {
  if (app.criticalPermissionCount === 0 && app.highPermissionCount === 0) {
    return <span className="text-text-helper">None high or critical</span>
  }
  return (
    <span className="flex flex-col gap-1">
      {app.criticalPermissionCount > 0 ? (
        <SeverityGlyph level="crit" size={12}>
          <span className="tabular-nums">{app.criticalPermissionCount}</span> critical
        </SeverityGlyph>
      ) : null}
      {app.highPermissionCount > 0 ? (
        <SeverityGlyph level="high" size={12}>
          <span className="tabular-nums">{app.highPermissionCount}</span> high
        </SeverityGlyph>
      ) : null}
    </span>
  )
}

function ActivityTag({ lastSignIn, isStale }: { lastSignIn?: string | null; isStale: boolean }) {
  if (!lastSignIn) {
    return (
      <Tag kind="outline" title="No sign-in on record">
        Unknown
      </Tag>
    )
  }
  if (isStale) {
    return (
      <Tag kind="high-contrast" title={`Last sign-in ${formatDate(lastSignIn)}`}>
        Stale
      </Tag>
    )
  }
  return <Tag title={`Last sign-in ${formatDate(lastSignIn)}`}>Active</Tag>
}

/** The review decision as a tag; a lapsed review says so; an app nobody reviewed reads "Not reviewed". */
function ReviewStatus({ status, expiresAt }: { status?: string; expiresAt?: string }) {
  if (!status) return <span className="whitespace-nowrap text-text-helper">Not reviewed</span>
  const isExpired = expiresAt ? new Date(expiresAt) < new Date() : false
  if (isExpired) {
    return (
      <Tag kind="high-contrast" title={`The ${status.toLowerCase()} review lapsed on ${formatDate(expiresAt!)}`}>
        Review expired
      </Tag>
    )
  }
  return <Tag kind={status === 'Flagged' ? 'high-contrast' : 'default'}>{status}</Tag>
}
